import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import { useForm } from 'react-hook-form';
import fieldValidations from 'helpers/fieldValidations';
import loginApi from 'api/requests/login';
import { AlertCircle, CheckCircle, Mail, RefreshCw } from 'lucide-react';

export default function SignUpConfirmEmail({ email, password, onConfirmed }) {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  async function confirmCode({ code }) {
    setError('');
    setIsLoading(true);
    try {
      await loginApi.confirmEmail(email, code);
      onConfirmed({ email, password });
    } catch (error) {
      if (error?.response?.status === 500) {
        setError(error?.response?.data?.message || 'Código inválido.');
      } else {
        setError('Ocorreu um erro. Verifique o código e tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function resendCode() {
    if (!canResend || resendLoading) return;
    setResendLoading(true);
    setResendSuccess(false);
    setError('');
    try {
      await loginApi.resendConfirmCode({ email, password });
      setResendSuccess(true);
      setCanResend(false);
      setCountdown(60);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      setError('Erro ao reenviar o código. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  }

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Container>
      <IconCircle>
        <Mail size={22} color={colors.occasionalPurple} />
      </IconCircle>
      <Title>Confirme seu e-mail</Title>
      <Subtitle>
        Enviamos um código de confirmação para <EmailHighlight>{email}</EmailHighlight>
      </Subtitle>

      {error && (
        <ErrorBanner>
          <AlertCircle size={16} color={colors.danger} />
          <Text color={colors.danger} name="small" type="bold">
            {error}
          </Text>
        </ErrorBanner>
      )}

      {resendSuccess && (
        <SuccessBanner>
          <CheckCircle size={16} color={colors.success} />
          <Text color={colors.success} name="small" type="bold">
            Código reenviado com sucesso!
          </Text>
        </SuccessBanner>
      )}

      <FormWrapper onSubmit={handleSubmit(confirmCode)}>
        <FieldGroup>
          <FieldLabel>
            Código de confirmação<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="Digite o código"
            name="code"
            register={register}
            validations={fieldValidations.code('código')}
          />
          {errors.code?.message && (
            <ErrorText>{errors.code.message}</ErrorText>
          )}
        </FieldGroup>

        {!canResend && (
          <CountdownRow>
            <CountdownText>
              Reenviar código em <CountdownTimer>{formatTime(countdown)}</CountdownTimer>
            </CountdownText>
          </CountdownRow>
        )}

        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <SubmitButton type="submit">
            <span>Confirmar</span>
            <CheckCircle size={18} />
          </SubmitButton>
        )}
      </FormWrapper>

      {canResend && (
        <ResendRow>
          <ResendText>Não recebeu o código?</ResendText>
          {resendLoading ? (
            <LoaderInline>
              <Loader />
            </LoaderInline>
          ) : (
            <ResendButton onClick={resendCode} type="button">
              <RefreshCw size={14} />
              <span>Reenviar código</span>
            </ResendButton>
          )}
        </ResendRow>
      )}
    </Container>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.25s ease-out;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3eaff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 4px;
  margin-bottom: 20px;
  text-align: center;
  line-height: 1.5;
`;

const EmailHighlight = styled.span`
  font-weight: 600;
  color: ${colors.nightDark};
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const FieldGroup = styled.div`
  width: 100%;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.nightDark};
  letter-spacing: 0.01em;
  margin-bottom: 6px;
`;

const Required = styled.span`
  color: ${colors.danger};
`;

const ErrorText = styled.p`
  color: ${colors.danger};
  font-size: 12px;
  margin-top: 4px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #fef2f2;
  border: 1px solid ${colors.dangerLight};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #f0fdf4;
  border: 1px solid ${colors.successLight};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const CountdownRow = styled.div`
  text-align: center;
`;

const CountdownText = styled.p`
  font-size: 13px;
  color: ${colors.night};
`;

const CountdownTimer = styled.span`
  font-weight: 600;
  color: ${colors.occasionalPurple};
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  margin: 4px auto 0;
  padding: 12px 28px;
  background: ${colors.secondaryPurple};
  color: ${colors.neutralLight};
  border: 1px solid ${colors.secondaryPurple};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 3px ${colors.nightLight};

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(105, 12, 228, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;

const LoaderInline = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0;
`;

const ResendRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${colors.neutralDark};
  width: 100%;
`;

const ResendText = styled.p`
  font-size: 13px;
  color: ${colors.night};
`;

const ResendButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1.5px solid ${colors.occasionalPurple}40;
  border-radius: 8px;
  padding: 8px 16px;
  color: ${colors.occasionalPurple};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3eaff;
    border-color: ${colors.occasionalPurple};
  }

  &:active {
    transform: scale(0.97);
  }
`;
